import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Empty, Row, Space, Spin, Table, Tabs, Tag, Typography, message } from "antd";
import { CheckCircleOutlined, FileTextOutlined, FunctionOutlined, PlusOutlined, PlayCircleOutlined, ReloadOutlined, SettingOutlined, WalletOutlined } from "@ant-design/icons";
import { ModalComponent, CancelButton } from "@comps/components";
import { makeRequest } from "@utils/helpers";
import dayjs from "dayjs";
import { convertTimeTotz, createSelectList } from "@comps/commonFunctions";
import { colors } from "../constants";
import { getDataByIdReq } from "./requests";
import UpdatePayroll from "./UpdatePayroll";
import RunPayroll from "./RunPayroll";
import CreateFormula from "../settings/formula/components/CreateFormula";
import CreatePayitem from "../settings/payitem/components/CreatePayitem";
import IndexFormula from "../settings/formula/IndexForumula";
import IndexPayitem from "../settings/payitem/IndexPayitem";
import IndexSalarySheets from "../salarySheet/IndexSalarySheets";
import IndexCompanyPayslip from "../companyPayslips/IndexCompanyPayslip";
import IndexCustomPayitem from "../settings/customPayitems/IndexCustomPayitem";
import PayrollPolicyCenter from "./PayrollPolicyCenter";
import "./payrollWorkspace.css";

const { Text } = Typography;

const statusColor = (statusClass) => ({
  background: colors[statusClass] || "#e5e7eb",
  color: colors[`${statusClass}-color`] || "#0f172a",
  border: 0,
});

const formatDateTime = (value, format = "YYYY MMM DD h:mm a") => (
  value ? convertTimeTotz(value).format(format) : "Not available"
);

const isManualEntryPayitem = (value) => (
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_") === "manual_entry"
);

const buildPayrollFilters = (record) => {
  if (!record?.id || !record?.history?.month) {
    return "";
  }

  const month = dayjs(record.history.month).format("YYYY-MM-DD");
  return `payroll_|${record.id};month_|${month};`;
};

const PayrollWorkspace = (props) => {
  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [childComponent, setChildComponent] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [payitems, setPayitems] = useState([]);
  const [activeCustomPayitemId, setActiveCustomPayitemId] = useState(null);
  const [activeTab, setActiveTab] = useState("setup");

  const payrollFilters = useMemo(() => buildPayrollFilters(props.record), [props.record]);
  const canOpenResults = Boolean(payrollFilters);

  useEffect(() => {
    getPayrollData();
    // eslint-disable-next-line
  }, [props.record?.id]);

  const getPayrollData = () => {
    setLoading(true);
    makeRequest(setLoader, getDataByIdReq, props.record.id, onGetPayrollSuccess, onError);
  };

  const onGetPayrollSuccess = (response) => {
    const payitemOptions = createSelectList(response.payitems || []);
    const mappedPayitems = (response.payroll?.payitems || []).map((item) => {
      const sourcePayitem = (response.payitems || []).find((payitem) => payitem.id === item.payitem_id);
      const selectedPayitem = payitemOptions.find((option) => option.value === item.payitem_id);
      return {
        ...item,
        key: item.id || item.payitem_id,
        payitem_name: item.payitem_name || selectedPayitem?.label || `Payitem #${item.payitem_id}`,
        calculation_type: item.calculation_type || sourcePayitem?.calculation_type || null,
      };
    });

    setPayitems(mappedPayitems);
    setPayrollData(response.payroll);
    const manualEntryPayitems = mappedPayitems.filter((item) => isManualEntryPayitem(item.calculation_type));
    setActiveCustomPayitemId((current) => current || manualEntryPayitems[0]?.payitem_id || null);
    setLoading(false);
  };

  const onError = (err, res) => {
    setLoading(false);
    message.error(res?.response?.data?.message || "Something went wrong");
  };

  const onRefreshAll = () => {
    getPayrollData();
    props.onRefresh?.();
  };

  const onChildCompleted = (data) => {
    if (data) {
      onRefreshAll();
    }
    setChildComponent(null);
  };

  const openEditPayroll = () => {
    setChildComponent(
      <UpdatePayroll
        onCompleted={ onChildCompleted }
        record={ props.record }
        disabled={ false }
      />
    );
  };

  const openRunPayroll = () => {
    setChildComponent(
      <RunPayroll
        onCompleted={ onChildCompleted }
        record={ props.record }
        authUser={ props.authUser }
        allData={ props.allData }
        disabled={ false }
      />
    );
  };

  const openCreateFormula = () => {
    setChildComponent(
      <CreateFormula
        onCompleted={ onChildCompleted }
        onSuccess={ onChildCompleted }
      />
    );
  };

  const openCreatePayitem = () => {
    setChildComponent(
      <CreatePayitem
        onCompleted={ onChildCompleted }
        onSuccess={ onChildCompleted }
        allData={ {} }
      />
    );
  };

  const openCustomPayitemTab = (payitemId) => {
    setActiveCustomPayitemId(payitemId);
    setActiveTab("custom-payitems");
  };

  const manualEntryPayitems = payitems.filter((item) => isManualEntryPayitem(item.calculation_type));

  const payrollPayitemColumns = [
    {
      title: "Payitem",
      dataIndex: "payitem_name",
      key: "payitem_name",
      render: (value, record) => (
        <Button
          type={ activeCustomPayitemId === record.payitem_id ? "primary" : "text" }
          onClick={ () => isManualEntryPayitem(record.calculation_type) && openCustomPayitemTab(record.payitem_id) }
          disabled={ !isManualEntryPayitem(record.calculation_type) }
        >
          { value }
        </Button>
      ),
    },
    {
      title: "Taxable",
      key: "is_taxable",
      render: (_, record) => record.is_taxable ? "Yes" : "No",
    },
    {
      title: "Arrear",
      key: "arrear_applicable",
      render: (_, record) => record.arrear_applicable ? "Yes" : "No",
    },
    {
      title: "Final Settlement",
      key: "is_final_settlement",
      render: (_, record) => record.is_final_settlement ? "Yes" : "No",
    },
    {
      title: "Custom Entry",
      key: "entry",
      render: (_, record) => (
        isManualEntryPayitem(record.calculation_type)
          ? (
            <Button type="link" onClick={ () => openCustomPayitemTab(record.payitem_id) }>
              Manage custom entries
            </Button>
          )
          : <Text type="secondary">Auto calculated</Text>
      ),
    },
  ];

  const currentCustomPayitem = manualEntryPayitems.find((item) => item.payitem_id === activeCustomPayitemId);

  const summaryItems = [
    {
      title: "Employees",
      value: props.record?.emp_count || 0,
      icon: <WalletOutlined />,
    },
    // {
    //   title: "Tax Structure",
    //   value: props.record?.tax_structure_name || "Not linked",
    //   icon: <SettingOutlined />,
    // },
    {
      title: "Payroll Month",
      value: props.record?.history?.month ? dayjs(props.record.history.month).format("YYYY MMMM") : "Not run yet",
      icon: <FileTextOutlined />,
    },
    {
      title: "Status",
      value: props.record?.history?.status || "Draft",
      icon: <CheckCircleOutlined />,
      statusClass: props.record?.history?.status_class || "default",
    },
  ];

  const resultEmpty = (
    <Empty
      description="Run this payroll first to open salary sheets and payslips here."
      image={ Empty.PRESENTED_IMAGE_SIMPLE }
    />
  );

  const footer = [
    <CancelButton key="close_button" onClick={ props.onCompleted } />,
  ];

  return (
    <ModalComponent
      top={ 20 }
      width={ 1600 }
      maskClosable={ false }
      visible={ true }
      footer={ footer }
      onCancel={ props.onCompleted }
      mainTitle={ props.record?.name || "Payroll Workspace" }
      subTitle={ "Payroll workspace" }
    >
      { childComponent }
      <div className="payroll-workspace">
        <div className="payroll-workspace__toolbar">
          <Space wrap>
            <Button icon={ <ReloadOutlined /> } onClick={ onRefreshAll }>Refresh workspace</Button>
            <Button icon={ <PlayCircleOutlined /> } type="primary" onClick={ openRunPayroll }>
              { props.record?.history?.status === "Running" ? "View run details" : "Run payroll" }
            </Button>
            <Button icon={ <SettingOutlined /> } onClick={ openEditPayroll }>Edit payroll setup</Button>
            <Button icon={ <FunctionOutlined /> } onClick={ openCreateFormula }>Add formula</Button>
            <Button icon={ <PlusOutlined /> } onClick={ openCreatePayitem }>Add payitem</Button>
          </Space>
        </div>

        <Spin spinning={ loading || loader }>
          <Row gutter={ [16, 16] } className="payroll-workspace__summary-row">
            { summaryItems.map((item) => (
              <Col xs={ 24 } sm={ 12 } xl={ 6 } key={ item.title }>
                <Card className="payroll-workspace__summary-card" bordered={ false }>
                  <Space align="start">
                    <span className="payroll-workspace__summary-icon">{ item.icon }</span>
                    <div>
                      <Text type="secondary">{ item.title }</Text>
                      <div className="payroll-workspace__summary-value">
                        { item.statusClass ? <Tag style={ statusColor(item.statusClass) }>{ item.value }</Tag> : item.value }
                      </div>
                    </div>
                  </Space>
                </Card>
              </Col>
            )) }
          </Row>

          {/* <Alert
            type="info"
            showIcon
            className="payroll-workspace__alert"
            message="HR workspace"
            description={
              `Manage payroll setup, dependent payitems and formulas, custom entries, salary sheets, and payslips without leaving this screen.`
            }
          /> */}

          <Tabs
            activeKey={ activeTab }
            onChange={ setActiveTab }
            centered
            items={ [
              {
                key: "setup",
                label: "Setup",
                children: (
                  <div className="payroll-workspace__section">
                    <Card bordered={ false } className="payroll-workspace__detail-card">
                      <Row gutter={ [16, 16] }>
                        <Col xs={ 24 } md={ 12 }>
                          <Text type="secondary">Running date</Text>
                          <div>{ formatDateTime(props.record?.history?.running_date) }</div>
                        </Col>
                        <Col xs={ 24 } md={ 12 }>
                          <Text type="secondary">Closing date</Text>
                          <div>{ formatDateTime(props.record?.history?.closing_date) }</div>
                        </Col>
                        <Col xs={ 24 } md={ 12 }>
                          <Text type="secondary">Description</Text>
                          <div>{ payrollData?.description || "No description added" }</div>
                        </Col>
                        <Col xs={ 24 } md={ 12 }>
                          <Text type="secondary">Process mode</Text>
                          <div>{ payrollData?.process_at_month_end ? "At month end" : `Processing day ${payrollData?.processing_date || "-"}` }</div>
                        </Col>
                      </Row>
                    </Card>

                    <Card
                      bordered={ false }
                      title="Payroll payitems"
                      extra={ <Button onClick={ openEditPayroll }>Update payitems</Button> }
                      className="payroll-workspace__detail-card"
                    >
                      <Table
                        rowKey="key"
                        columns={ payrollPayitemColumns }
                        dataSource={ payitems }
                        pagination={ false }
                        locale={ { emptyText: "No payitems linked with this payroll yet." } }
                      />
                    </Card>
                  </div>
                ),
              },


              {
                key: "salary-sheet",
                label: "Salary Sheet",
                children: canOpenResults
                  ? <IndexSalarySheets embedded initialFilters={ payrollFilters } modkey={ props.modkey } />
                  : resultEmpty,
              },
              {
                key: "payslips",
                label: "Payslips",
                children: canOpenResults
                  ? <IndexCompanyPayslip embedded initialFilters={ payrollFilters } modkey={ props.modkey } />
                  : resultEmpty,
              },
              {
                key: "custom-payitems",
                label: "Custom Payitems",
                children: (
                  <div className="payroll-workspace__section">
                    <Alert
                      type="warning"
                      showIcon
                      className="payroll-workspace__alert payroll-workspace__alert--soft"
                      message="Custom payitem entries"
                      description="Only payitems with manual entry are shown here so HR can manage employee-wise custom amounts safely."
                    />
                    <Card bordered={ false } className="payroll-workspace__detail-card">
                      <Space wrap>
                        { manualEntryPayitems.length > 0 ? manualEntryPayitems.map((item) => (
                          <Button
                            key={ item.key }
                            type={ activeCustomPayitemId === item.payitem_id ? "primary" : "default" }
                            onClick={ () => setActiveCustomPayitemId(item.payitem_id) }
                          >
                            { item.payitem_name }
                          </Button>
                        )) : <Text type="secondary">No manual-entry payitems are linked with this payroll.</Text> }
                      </Space>
                    </Card>

                    { currentCustomPayitem ? (
                      <IndexCustomPayitem
                        embedded
                        modkey={ props.modkey }
                        payitemId={ currentCustomPayitem.payitem_id }
                        payitemName={ currentCustomPayitem.payitem_name }
                      />
                    ) : (
                      <Empty description="No manual-entry payitem is available for custom entry management." image={ Empty.PRESENTED_IMAGE_SIMPLE } />
                    ) }
                  </div>
                ),
              },

              {
                key: "formulas",
                label: "Formulas",
                children: <IndexFormula modkey={ props.modkey } />,
              },
              {
                key: "payitems",
                label: "Payitems",
                children: <IndexPayitem modkey={ props.modkey } />,
              },

              {
                key: "policies",
                label: "Policies",
                children: <PayrollPolicyCenter modkey={ props.modkey } />,
              },
            ] }
          />
        </Spin>
      </div>
    </ModalComponent>
  );
};

export default PayrollWorkspace;
